import React from 'react'
import { observer } from 'mobx-react'

import { StepContainer } from 'jobs/creation/OutlineViewModel'
import NameInputViewModel from 'jobs/creation/NameViewModel'
import NameInput from '../../JobCreation/JobCreationFormView/NameInput'
import WorkspaceSelectorViewModel from 'jobs/creation/WorkspaceSelectorViewModel'
import CompactWorkspaceSelector from '../../JobCreation/JobCreationFormView/CompactWorkspaceSelector'
import OwnerSelectorViewModel from 'jobs/creation/OwnerSelectorViewModel'
import CompactOwnerSelector from '../../JobCreation/JobCreationFormView/CompactOwnerSelector'
import TeamSelectorViewModel from 'jobs/creation/TeamSelectorViewModel'
import CompactTeamSelector from '../../JobCreation/JobCreationFormView/CompactTeamSelector'
import RequestProviderSelectorViewModel from 'jobs/creation/RequestProviderSelectorViewModel'
import CompactRequestProviderSelector from '../../JobCreation/JobCreationFormView/CompactRequestProviderSelector'
import LegendSelectorViewModel from 'jobs/creation/LegendSelectorViewModel'
import CompactLegendSelector from '../../JobCreation/JobCreationFormView/CompactLegendSelector'
import { QuestionStepViewModel } from 'jobs/creation/IntakeQuestionsViewModel'
import PreviousProviderSelectionViewModel from 'jobs/creation/PreviousProviderSelectionViewModel'
import PreviousProviderSelector from '../../JobCreation/PreviousProviderSelector'
import { getQuestionComponentRegistryEntry } from 'jobs/questions/component-registry'
import JobCreationViewModel from 'jobs/creation/JobCreationViewModel'
import Step from '../../JobCreation/JobCreationFormView/Step'
import { useObservableCallback } from 'hooks/rx-hooks'
import styles from './JobCreationQuestions.sass'
import Spinner from 'components/Spinner'
import { ModalType } from '../JobCreationDialogViewModel'

interface JobQuestionsProps {
  vm: JobCreationViewModel
  jobWasCreated: boolean
  modalType: ModalType
}

export default observer(function JobCreationQuestions({
  vm,
  jobWasCreated,
  modalType,
}: JobQuestionsProps) {
  const steps = vm.stepContainers.map((stepContainer) => {
    const stepContent = renderStepContent(
      stepContainer,
      jobWasCreated,
      modalType,
    )
    if (!stepContent) {
      return null
    }

    return (
      <StepView
        key={stepContainer.step.stepId}
        stepContainer={stepContainer}
        modalType={modalType}
      >
        {stepContent}
      </StepView>
    )
  })

  return (
    <div className={styles.root}>
      {steps}

      {(vm.intakeQuestions.querying ||
        vm.legendSelector.isFetchingLegendDetails ||
        vm.persister.persisting) && (
        <div className={styles.loadingSpinner}>
          <LoadingFormContent />
        </div>
      )}
    </div>
  )
})

interface StepViewProps {
  stepContainer: StepContainer
  modalType: ModalType
  children: React.ReactNode
}

/**
 * Container for Question Steps, renders the proper content based on the container type.
 */
export const StepView = observer(function StepView({
  stepContainer,
  modalType,
  children,
}: StepViewProps) {
  const step = stepContainer.step

  const isNotValid =
    modalType === 'EditDraft' &&
    stepContainer instanceof QuestionStepViewModel &&
    stepContainer.question.type !== 'BREAKER' &&
    stepContainer.question.type !== 'DOCUMENTS' &&
    !step.valid

  useObservableCallback(step.becomeActive$, step.onBecameActive, [
    step.becomeActive$,
  ])

  return (
    <Step
      title={step.outlineDisplay.title}
      isLast={step.position.last}
      descriptionHtml={step.descriptionHtml}
      active={step.isActive}
      becomeActive$={step.becomeActive$}
      onBecomeVisible={step.becomeVisible}
      onBecomeHidden={step.becomeHidden}
      isNotValid={isNotValid}
      data-test={`step-${step.stepId}`}
      compact
    >
      {children}
    </Step>
  )
})

/**
 * Each step container represents a piece of the creation UI and its' associated view model.
 *
 * @param stepContainer
 * @param jobWasCreated
 * @param modalType
 */
export function renderStepContent(
  stepContainer: StepContainer,
  jobWasCreated: boolean,
  modalType: ModalType,
) {
  if (stepContainer instanceof NameInputViewModel) {
    return <NameInput vm={stepContainer} />
  }

  if (stepContainer instanceof WorkspaceSelectorViewModel) {
    return <CompactWorkspaceSelector vm={stepContainer} />
  }

  if (stepContainer instanceof OwnerSelectorViewModel) {
    return <CompactOwnerSelector vm={stepContainer} />
  }

  if (stepContainer instanceof TeamSelectorViewModel) {
    return <CompactTeamSelector vm={stepContainer} />
  }

  if (stepContainer instanceof RequestProviderSelectorViewModel) {
    return <CompactRequestProviderSelector vm={stepContainer} />
  }

  if (stepContainer instanceof LegendSelectorViewModel) {
    return <CompactLegendSelector vm={stepContainer} disabled={jobWasCreated} />
  }

  if (stepContainer instanceof QuestionStepViewModel) {
    const { EditorInputComponent } = getQuestionComponentRegistryEntry(
      stepContainer.question.type,
    )
    return (
      <EditorInputComponent
        vm={stepContainer.question}
        disabled={stepContainer.disabled}
        focus$={stepContainer.step.becomeActive$}
        onFocus={stepContainer.step.onBecameActive}
        isNotValid={modalType === 'EditDraft' && !stepContainer.step.valid}
      />
    )
  }

  if (stepContainer instanceof PreviousProviderSelectionViewModel) {
    return <PreviousProviderSelector vm={stepContainer} />
  }
}

/**
 * Component that renders a loader in the form.
 */
function LoadingFormContent() {
  return (
    <div className={styles.loading}>
      <Spinner />
    </div>
  )
}
